import { useEffect, useState } from "react";
import { Typography, Box } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useNavigate, useLocation } from "react-router";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import UsersTable from "../../components/tables/UsersTable.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "components/MDDForm/components/ContentDialog.jsx";
import { Form, FormRenderer } from "../../components/MDDForm/MDDForm.jsx";
import { fdefUploadBenutzer } from "../../formdefs/upload_benutzer.js";
import { getRWObjectCopy } from "../../components/MDDForm/helper.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUserActions } from "../../hooks/useUserActions.jsx";
import AdminUserForm from "./components/AdminUserForm.jsx";
import UserImportHelp from "../../components/UserImportHelp.jsx";
import AssignmentImportTabPage from "./AssignmentImportTabPage.jsx";
import { useSnackbar } from "notistack";
import { useUser } from "@accredo/fe-shared/hooks/user.js";

/**
 * Users List Page
 * Shows a list of users with create/edit/import functionality
 */
export default function UserIndexPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();
  const { createOrUpdateUser, uploadUserList } = useUserActions();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState("1");

  const is_sys_admin = user?.role_names?.includes("sysadmin");
  const is_customer_admin = user?.role_names?.includes("kd_verwaltung");
  const can_manage_users = !!(is_sys_admin || is_customer_admin);
  const can_import_users = !!is_sys_admin;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Benutzer", path: "/admin/users" },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (location.pathname === "/admin/users/assignments") {
      setTabValue("2");
    } else {
      setTabValue("1");
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    switch (newValue) {
      case "1":
        navigate("/admin/users");
        break;
      case "2":
        navigate("/admin/users/assignments");
        break;
      default:
        setTabValue(newValue);
    }
  };

  const closeDialog = () => {
    setOpenCD(false);
  };

  const openEditUserModal = (userData) => {
    if (!can_manage_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }
    showEditCreateModal(userData, "Benutzer bearbeiten");
  };

  const openCreateUserModal = () => {
    if (!can_manage_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }
    showEditCreateModal({}, "Benutzer erstellen");
  };

  const showEditCreateModal = (model, title) => {
    setOpenCD(true);
    setTca({
      title: title,
      content: (
        <AdminUserForm
          initialData={model}
          isEditMode={!!model.id}
          onSubmit={async (formData, formMethods) => {
            try {
              await createOrUpdateUser(formData, formMethods.setError);
              closeDialog();
              enqueueSnackbar("Änderungen gespeichert.", {
                variant: "success",
              });
              return true;
            } catch {
              // Validation errors (e.g. Email bereits vergeben) wurden bereits
              // per setError am betroffenen Feld angezeigt; Dialog bleibt offen.
              return false;
            }
          }}
          onCancel={undefined}
          hideActions={true}
        />
      ),
      actions: null,
    });
  };

  const openImportUsersModal = () => {
    if (!can_import_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }

    let fdef = getRWObjectCopy(fdefUploadBenutzer);
    setCDActions(null);
    setOpenCD(true);
    setTca({
      title: "Upload Benutzer für Kunde",
      content: (
        <>
          <Form>
            <FormRenderer
              fdef={fdef}
              model={{}}
              variant="filled"
              editMode={true}
              showActions={false}
              showCancel={true}
              showToggleEdit={false}
              showErrorText={true}
              actionsPosition="bottom"
              apiGet={backendGet}
              cancelAction={closeDialog}
              submitAction={{
                method: "callback",
                callback: async (formData) => {
                  await uploadUserList(formData);
                  closeDialog();
                  enqueueSnackbar("Benutzer wurden importiert.", {
                    variant: "success",
                  });
                  // Note: For bulk imports, the table will need to refetch
                  // This is handled by triggering a data reload
                  window.location.reload();
                },
              }}
              setActions={setCDActions}
            />
          </Form>
          <UserImportHelp />
        </>
      ),
      actions: null,
    });
  };

  return (
    <>
      <Box>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" gutterBottom>
            Benutzerverwaltung
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Benutzer und Vertragszuordnungen verwalten
          </Typography>
        </Box>

        <Box sx={{ width: "100%", typography: "body1" }}>
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleTabChange} aria-label="user admin tabs">
                <Tab label="Benutzer" value="1" />
                <Tab label="Vertragszuordnung" value="2" />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ paddingX: 0 }}>
              <UsersTable
                onRowEdit={openEditUserModal}
                onClickNew={openCreateUserModal}
                onClickImport={openImportUsersModal}
              />
            </TabPanel>
            <TabPanel value="2" sx={{ paddingX: 0 }}>
              <AssignmentImportTabPage />
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        CDActions={CDActions}
        tca={tca}
      />
    </>
  );
}
